// ─────────────────────────────────────────────────────────────────────────────
//  Crown-priced premium cosmetics
//  Distinct from shopItems.js (Stripe-priced items).
//  Prices here are in Crowns, not cents.
// ─────────────────────────────────────────────────────────────────────────────

export const RARITY = {
  COMMON:  'common',   // 300–500 crowns
  RARE:    'rare',     // 800–1200 crowns
  PREMIUM: 'premium',  // 1500–2500 crowns
};

export function getRarityLabel(rarity) {
  const map = { common: 'Common', rare: 'Rare', premium: 'Premium' };
  return map[rarity] || rarity;
}

export function getRarityColor(rarity) {
  return rarity === 'premium' ? '#EDBB4A'
       : rarity === 'rare'    ? '#7366F0'
       :                        '#6B7280';
}

// ─────────────────────────────────────────────────────────────────────────────
//  Cosmetic item definitions
//  effects[] keys map to CSS classes / renderer flags in the effects system.
//  preview contains enough data for the shop preview renderer.
// ─────────────────────────────────────────────────────────────────────────────

export const COSMETIC_ITEMS = [
  // ── Piece sets ─────────────────────────────────────────────────────────────

  {
    id: 'crowned_knight',
    name: 'Crowned Knight',
    desc: 'Armored obsidian pieces crowned with violet light — the flagship set.',
    category: 'pieces',
    rarity: RARITY.PREMIUM,
    price: 2000,
    effects: ['glow_violet', 'trail_shimmer', 'check_pulse', 'checkmate_glow'],
    preview: {
      pieceColor:  '#C8B8FF',
      darkColor:   '#2A1E5C',
      glowColor:   'rgba(147,130,255,0.65)',
      trailColor:  'rgba(147,130,255,0.5)',
    },
    featured: true,
    permanent: true,
  },

  {
    id: 'crystal_set',
    name: 'Crystal Set',
    desc: 'Semi-transparent pieces that shatter on capture.',
    category: 'pieces',
    rarity: RARITY.RARE,
    price: 1200,
    effects: ['glass_render', 'capture_shatter'],
    preview: {
      pieceColor:  'rgba(210,240,255,0.82)',
      darkColor:   'rgba(60,100,160,0.82)',
      glowColor:   'rgba(180,220,255,0.45)',
    },
    permanent: true,
  },

  {
    id: 'glitch_set',
    name: 'Glitch Set',
    desc: 'Digital distortion, chromatic split — for the dangerous player.',
    category: 'pieces',
    rarity: RARITY.PREMIUM,
    price: 1750,
    effects: ['glitch_flicker', 'rgb_split'],
    preview: {
      pieceColor:  '#D0C8FF',
      darkColor:   '#1A0A3C',
      glowColor:   'rgba(200,0,255,0.4)',
    },
    permanent: true,
  },

  // ── Boards ─────────────────────────────────────────────────────────────────

  {
    id: 'neon_grid',
    name: 'Neon Grid',
    desc: 'Electrified grid lines with a slow violet pulse.',
    category: 'board',
    rarity: RARITY.RARE,
    price: 1000,
    effects: ['neon_lines', 'grid_flicker'],
    preview: {
      lightSquare: '#0F0F1C',
      darkSquare:  '#07070F',
      gridColor:   '#7B5CF0',
      glowColor:   'rgba(123,92,240,0.55)',
    },
    permanent: true,
  },

  {
    id: 'minimal_noir',
    name: 'Minimal Noir',
    desc: 'High-contrast black and white. Clean and uncompromising.',
    category: 'board',
    rarity: RARITY.COMMON,
    price: 450,
    effects: [],
    preview: {
      lightSquare: '#EFEFEF',
      darkSquare:  '#111111',
      gridColor:   'transparent',
    },
    permanent: true,
  },

  {
    id: 'arcane_flames',
    name: 'Arcane Flames',
    desc: 'Ancient stone board with fire-touched runes at its edges.',
    category: 'board',
    rarity: RARITY.PREMIUM,
    price: 1800,
    effects: ['fire_glow', 'rune_pulse'],
    preview: {
      lightSquare: '#3D2A14',
      darkSquare:  '#1E1408',
      gridColor:   'rgba(255,140,0,0.45)',
    },
    permanent: true,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
//  Crown bundle definitions (mirrors server CROWN_BUNDLES)
// ─────────────────────────────────────────────────────────────────────────────

export const CROWN_BUNDLES = [
  { id: 'crowns-500',  crowns: 500,  price: 4.99,  bonus: null,  label: '500 👑' },
  { id: 'crowns-1100', crowns: 1100, price: 9.99,  bonus: '+10%', label: '1,100 👑' },
  { id: 'crowns-2500', crowns: 2500, price: 19.99, bonus: '+25%', label: '2,500 👑' },
];
